import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type Theme = 'dark' | 'light' | 'system'

interface UIState {
  theme: Theme
  sidebarCollapsed: boolean
  /** The sidebar categories left open; the rest show only their heading. */
  openNavGroups: string[]
  commandPaletteOpen: boolean
  density: 'comfortable' | 'compact'

  setTheme: (theme: Theme) => void
  toggleSidebar: () => void
  setSidebarCollapsed: (collapsed: boolean) => void
  toggleNavGroup: (id: string) => void
  openNavGroup: (id: string) => void
  setCommandPaletteOpen: (open: boolean) => void
  setDensity: (density: 'comfortable' | 'compact') => void
}

function applyTheme(theme: Theme) {
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
  const dark = theme === 'dark' || (theme === 'system' && prefersDark)
  document.documentElement.classList.toggle('dark', dark)
  // Read by the inline script in index.html on the next load, so the page
  // never paints the wrong theme before hydration.
  try {
    localStorage.setItem('unity.theme', theme === 'system' ? (dark ? 'dark' : 'light') : theme)
  } catch {
    /* private browsing — the in-memory value still applies for this session */
  }
}

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      theme: 'dark',
      sidebarCollapsed: false,
      openNavGroups: [],
      commandPaletteOpen: false,
      density: 'comfortable',

      setTheme: (theme) => {
        applyTheme(theme)
        set({ theme })
      },
      toggleSidebar: () => set({ sidebarCollapsed: !get().sidebarCollapsed }),
      setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
      toggleNavGroup: (id) => {
        const open = get().openNavGroups
        set({
          openNavGroups: open.includes(id) ? open.filter((group) => group !== id) : [...open, id],
        })
      },
      openNavGroup: (id) => {
        const open = get().openNavGroups
        if (!open.includes(id)) set({ openNavGroups: [...open, id] })
      },
      setCommandPaletteOpen: (commandPaletteOpen) => set({ commandPaletteOpen }),
      setDensity: (density) => set({ density }),
    }),
    {
      name: 'unity.ui',
      partialize: (state) => ({
        theme: state.theme,
        sidebarCollapsed: state.sidebarCollapsed,
        openNavGroups: state.openNavGroups,
        density: state.density,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) applyTheme(state.theme)
      },
    },
  ),
)

/** Keep "system" theme reactive to OS changes while the console is open. */
export function watchSystemTheme() {
  const media = window.matchMedia('(prefers-color-scheme: dark)')
  const handler = () => {
    if (useUIStore.getState().theme === 'system') applyTheme('system')
  }
  media.addEventListener('change', handler)
  return () => media.removeEventListener('change', handler)
}
